import React, { useState, useEffect, useCallback } from 'react';
import FollowerRange from '@sg/components/FollowerRange';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';
import { getProjectOptionsList, addProjectOptions } from '@sg/services/index';
import { messageTips } from '@gp/utils/common';

interface ProjectSelectComponentProps {
    value: any;
    record: any;
    editData?: any[];
    stableUpdater?: (value: any) => void;
    hasError?: boolean;
    startDate?: string;
    endDate?: string;
}

const ProjectSelectComponent: React.FC<ProjectSelectComponentProps> = ({
    value,
    record,
    editData,
    stableUpdater,
    hasError = false,
    startDate,
    endDate
}) => {
    // 判断是否是新增的行（通过 id 是否有 new_ 前缀判断）
    const isNewRow = record.id && String(record.id).startsWith('new_');

    // 如果不是新增行，直接显示文本
    if (!isNewRow) {
        return <div className='dbs-project-title' style={{ marginRight: '10px' }}>{record.name || '-'}</div>;
    }

    const [projectOptions, setProjectOptions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchGameList = useCallback(async (searchTerm?: string) => {
        try {
            setLoading(true);
            // If search term is provided, use search; otherwise fetch top 10
            const payload: any = searchTerm
                ? { search: searchTerm }
                : { top_num: 10 };

            // 添加 start_date 和 end_date（如果提供）
            if (startDate) {
                payload.start_date = startDate;
            }
            if (endDate) {
                payload.end_date = endDate;
            }

            const response = await getProjectOptionsList(payload);
            const raw = response?.data || [];

            // Set project options for dropdown (includes cover for icons)
            const games = raw.map((game: any) => ({
                text: game.name,
                value: game.game_id,
                cover: game.cover, // Include cover for display
            }));
            setProjectOptions(games);

            // Return the games for immediate use
            return games;
        } catch (err: any) {
            console.error('Error fetching game list:', err);
            messageTips('error', 'Failed to fetch game list');
            return [];
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    // 组件创建时自动加载初始数据
    useEffect(() => {
        fetchGameList();
    }, [fetchGameList]);

    // Handle search in project dropdown - wrapped in useCallback to prevent infinite loop
    const handleProjectSearch = useCallback((searchTerm: string) => {
        // Only search if there's a search term, otherwise fetch default list
        if (searchTerm && searchTerm.trim()) {
            fetchGameList(searchTerm.trim());
        } else {
            fetchGameList(); // Fetch default top 10
        }
    }, [fetchGameList]);

    const handleChange = (newValue: any) => {
        // projectOptions 格式是 {text, value, cover}
        const selectedProject = projectOptions.find((opt: any) => opt.value === newValue);

        if (stableUpdater) {
            if (selectedProject) {
                // 从列表选择：使用 text 作为 name，value 作为 game_id
                stableUpdater({
                    name: selectedProject.text,
                    game_id: selectedProject.value,
                    cover: selectedProject.cover || GameDefault
                });
                addProjectOptions({
                    game_id: selectedProject.value,
                });
            } else {
                // 自定义输入：使用输入值作为 name，game_id 为 0
                stableUpdater({
                    name: newValue,
                    game_id: 0,
                    cover: GameDefault
                });
            }
        }
    };

    return (
        <div style={{
            display: 'inline-block'
        }}>
            <FollowerRange
                className={`dbs-follower-range dbs-project-title-select ${hasError ? 'ds-timeline-error' : ''}`}
                overlayClassName="dbs-follower-range"
                value={value}
                onChange={handleChange}
                options={projectOptions}
                searchable
                searchPlaceholder="Search project"
                isCustomize={true}
                appearance="default"
                size="auto"
                boxStyle={{ width: '276px' }}
                loading={loading}
                onSearch={handleProjectSearch} // Server-side search callback
                placeholder={loading ? 'Loading projects...' : 'Select Project'}
                placeholderStyle={{ fontWeight: 400 }}
            />
        </div>
    );
};

export default ProjectSelectComponent;
